/**
 * The daily round's part of the review screen (v1 ticket 15, variant D). The review screen runs the round's cards
 * as it runs any review; this controller adds the town around them.
 *
 * - The learner's own town lies behind the round (`<round-town>`, its own chunk), hidden while a full-screen card
 *   covers it.
 * - A review card that is passed banks a brick, "word → material", at the material the server saved the word at; a
 *   miss banks nothing. The server moves the box on save as always: the bank only holds back the town's picture.
 * - A first look flies the camera to the word's lot in the fog and hangs the card under it as a sign; a pass lifts
 *   the fog there and then.
 * - "Build what I earned" (after the last card, or after Stop here) plays the payoff for the bricks banked; a round
 *   with none goes straight on. Then the town after the round stays on screen under the end's sheet, with a chip
 *   over each lot that changed (`endChips`, round-town.ts `showChips`). A street whose every word became known is
 *   paved there with a warm sweep, after the buildings. A round left without it has changed the town all the same.
 * - A tending round (v1 ticket 27) has no town behind its cards; at its end the town after it is drawn the same way
 *   (`tendEnd`), with its chips, its newly known streets paved first (`paveNew`).
 *
 * A moment's first lesson in the town (v1 ticket 41, land-view.ts) runs here too, over the landing's own town
 * (`useTown`): a card whose word is in the fog on screen when it comes up hangs as a sign under its lot, and a pass
 * lifts the fog (the reply's `leftFog`); a miss keeps it, with no easier card. Any other card covers the town, and
 * banks a brick only when the answer made its word climb a material (the reply's `climbed`, round-plan.ts
 * `lessonBanks`): a flip, a reveal, a skip, "I said it", a pass that kept the word where it was and a fog lifted
 * build nothing.
 *
 * A building tended from home's town (`useSigns`) runs here too: every card hangs as a sign under the building's lot,
 * the easier card after a miss included, and what an answer changed shows on the spot (`RoundTown.settle`): its fog
 * parts, or it goes up to the material it climbed to. No bricks and no payoff.
 *
 * No box ever shows, and nothing here calls a model: it reads the town (`GET /api/town`) and the attempts' replies.
 */
import { html, nothing, type ReactiveController, type ReactiveControllerHost } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { api } from '../api';
import { feel } from '../feel';
import { num, t } from '../i18n';
import { brickLabel } from '../town/brick-label';
import { lotArea, signSpot, type SignSpot } from '../town/camera';
import { hangsAsSign, withoutDistricts } from '../town/landing';
import { endChips, type EndChip } from '../town/end-summary';
import type { OwnTown } from '../town/own-town';
import { bank, banks, brickOf, lessonBanks, type Brick } from '../town/round-plan';
import type { RoundTown } from '../town/round-town';
import { dawnSky, skyOf } from '../town/sky';
import { streetsFrom } from '../town/streets';
import type { AttemptInput, AttemptResult, ReviewQueueEntry } from '../types';

type Host = ReactiveControllerHost & HTMLElement;

export class RoundMode implements ReactiveController {
  /** The town before this sitting's first answer: what a brick's material is measured against, and the changes. */
  before: OwnTown | null = null;
  /** Bricks banked this sitting, one per word, at its latest material. */
  bricks: Brick[] = [];
  /** The latest brick, shown on the cards that follow. */
  latest: Brick | null = null;
  /** The card hanging as a sign under its lot (a first look), and where; null while cards cover the town. */
  sign: { i: number; spot: SignSpot } | null = null;
  /** The sign's first look was passed: its fog lifted. */
  lifted = -1;
  /** The learner stopped the round before its last card. */
  stopped = false;
  /** Every answer of the round has landed (or failed). */
  settled = false;
  /** The latest late-save refresh (`refreshChanges`): an older read never lands over a newer one. */
  private refreshGen = 0;
  /** The cards; the payoff (or the town after being read); the end over the town. */
  phase: 'cards' | 'building' | 'built' = 'cards';
  /** The town after the round, once read; null when it could not be. What the end's chips are measured against. */
  after: OwnTown | null = null;
  /** The town's chunk has loaded, so `<round-town>` renders. */
  loaded = false;
  /** A moment's first lesson over the landing's town, not the daily round (`useTown`). */
  lesson = false;
  /** A building tended as a sign on it (`useSigns`): every card hangs under its lot. */
  signs = false;
  /** Lesson words whose fog lifted in this lesson: their cards cover the town now (and climb nothing more today). */
  private liftedKeys = new Set<string>();
  /** Lesson cards that came up on a word in the fog: signs under their lots (while the town is on screen). */
  private fogCards = new WeakSet<ReviewQueueEntry>();
  /** The landing's town, which the first lesson runs over; the round draws its own. */
  private external: RoundTown | null = null;
  /** The town started (D or B); null while it starts. */
  private ok: boolean | null = null;
  private ready: Promise<boolean> | null = null;
  private alive = true;
  /**
   * What the sitting did for the dawn at its end (set by the review view): the daily round's cards done today before
   * it and its total (`GET /api/round`: `done`, and `done` plus the queue), the cards reached and passed so far.
   */
  sitting: { done0: number; total: number; reached: () => number; passed: () => number } | null = null;
  /** The town's moves, one after another: a fog lifting, then the flight to the next lot. */
  private chain: Promise<unknown> = Promise.resolve();

  constructor(private host: Host) {
    host.addController(this);
  }

  hostConnected() { this.alive = true; }
  hostDisconnected() { this.alive = false; }

  /** The town on screen behind the round (the landing's, for a first lesson), or null. */
  get townEl(): RoundTown | null {
    return this.town;
  }

  private get town(): RoundTown | null {
    return this.external ?? this.host.querySelector('round-town');
  }

  private then(step: () => Promise<unknown> | unknown) {
    this.chain = this.chain.then(step).catch(() => {});
    return this.chain;
  }

  /** Draws the town behind the round, covered by its cards, once per sitting, under `sky` (its own by default); whether it started. */
  begin(before: OwnTown | null, sky?: number): Promise<boolean> {
    if (this.ready) return this.ready;
    this.before = before;
    if (!before?.districts.length) {
      this.ok = false;
      return (this.ready = Promise.resolve(false));
    }
    return (this.ready = new Promise<boolean>((done) => void this.then(async () => {
      let ok = false;
      try {
        await import('../town/round-town');
        this.loaded = true;
        this.host.requestUpdate();
        await this.host.updateComplete;
        const el = this.town;
        if (el && this.alive) {
          el.covered = !this.sign;
          ok = await el.start(before, sky ?? skyOf(before));
        }
      } catch (e) {
        console.warn('round town:', e instanceof Error ? e.message : e);
      }
      this.ok = ok;
      this.host.requestUpdate();
      done(ok);
    })));
  }

  /**
   * A moment's first lesson runs over the town its landing drew (`el`, null when none could start), from the town
   * on screen then (`before`).
   */
  useTown(el: RoundTown | null, before: OwnTown | null): Promise<boolean> {
    this.lesson = true;
    this.external = el;
    this.before = before;
    this.ok = !!el?.stage;
    return (this.ready = Promise.resolve(this.ok));
  }

  /** A building tended from home's town: its cards hang as signs over the town drawn behind them (`begin`). */
  useSigns(el: RoundTown | null = null, before: OwnTown | null = null): void {
    this.signs = true;
    if (el) {
      this.external = el;
      this.before = before;
      this.ok = !!el.stage;
      this.ready = Promise.resolve(this.ok);
    }
  }

  /** The round moved to card `i`: a first look hangs under its lot in the town; any other card covers the town. */
  enter(i: number, e: ReviewQueueEntry | undefined): void {
    this.lifted = -1;
    const el = this.town;
    const fog = !!e && (this.signs || (this.lesson ? hangsAsSign(e.item.key, e.task.type, el?.shown ?? this.before, this.liftedKeys) : !!e.firstLook));
    if (fog && e && this.lesson) this.fogCards.add(e);
    if (!fog || !e || this.ok !== true || !el) {
      this.sign = null;
      if (el) el.covered = true;
      this.host.requestUpdate();
      return;
    }
    // The sign hangs at once, under the strip the lot is framed in; the flight only turns its pointer to the lot.
    const w = innerWidth, a = lotArea(w, innerHeight);
    const spot = signSpot(w / 2, a.y + a.h, w);
    this.sign = { i, spot };
    scrollTo(0, 0);
    el.covered = false;
    this.host.requestUpdate();
    void this.then(async () => {
      if (!this.alive || this.sign?.i !== i) return;
      const s = await el.toLot(e.item.key);
      if (!s || this.sign?.i !== i) return;
      const px = Math.round(Math.max(18, Math.min(spot.width - 18, s.left + s.px - spot.left)));
      this.sign = { i, spot: { ...spot, px } };
      this.host.requestUpdate();
    });
  }

  /** An answer landed: a scored pass on a review card banks a brick; a first look passed lifts its fog there. */
  saved(i: number, e: ReviewQueueEntry, a: AttemptInput, r: AttemptResult): void {
    const key = e.item.key;
    if (this.signs) {
      if (!r.leftFog && !r.climbed) return;
      if (r.leftFog && this.sign?.i === i) this.lifted = i;
      this.host.requestUpdate();
      void this.then(async () => {
        const after = await this.townNow();
        if (this.alive) await this.town?.settle(key, after);
      });
      return;
    }
    if (this.lesson ? this.fogCards.has(e) || !!r.leftFog : e.firstLook) {
      if (r.item.box <= 0) return;
      if (this.lesson) this.liftedKeys.add(key);
      if (this.sign?.i === i) this.lifted = i;
      this.host.requestUpdate();
      void this.then(async () => {
        const after = await this.townNow();
        if (this.alive) await this.town?.liftFog(key, after);
      });
      return;
    }
    const answer = {
      spelling: e.spelling, correct: a.correct, revealed: a.revealed, skipped: a.skipped, selfReport: a.selfReport, graded: r.graded,
    };
    // The round banks every scored pass; the first lesson only a climb, as the server saved it.
    if (!(this.lesson ? lessonBanks(answer, r) : banks(answer))) return;
    const was = this.before?.buildings.find((b) => b.key === key)?.material;
    this.latest = brickOf(key, e.item.latin || e.item.ca, r.item.box, was);
    this.bricks = bank(this.bricks, this.latest);
    feel('brick');
    this.host.requestUpdate();
  }

  /** Stop here: the round ends where it is, and its bricks so far wait for the payoff. */
  stop(): void {
    this.stopped = true;
    this.cover();
  }

  private cover() {
    this.sign = null;
    const el = this.town;
    if (el) el.covered = true;
    this.host.requestUpdate();
  }

  /** The cards are over: the town as it stood comes back into view above the end's panel, its bricks still to build. */
  private rest() {
    this.cover();
    if (this.ok !== true) return;
    void this.then(async () => {
      await this.host.updateComplete;
      const el = this.town, panel = this.host.querySelector('.round-after');
      if (el && this.alive && this.phase === 'cards') await el.rest(panel?.getBoundingClientRect().top ?? innerHeight);
    });
  }

  /** Another pass at the round (today's requirement still open): a payoff already played starts a new bank. */
  again(): void {
    if (this.phase === 'built') {
      this.bricks = [];
      this.latest = null;
    }
    this.stopped = false;
    this.settled = false;
    this.phase = 'cards';
    this.after = null;
    this.town?.clearChips();
  }

  /** The round's cards are over: once its answers land, a round with no bricks goes straight to the town after it. */
  async finish(landed: Promise<unknown>): Promise<void> {
    this.rest();
    await landed;
    this.settled = true;
    this.host.requestUpdate();
    if (!this.bricks.length) await this.build(Promise.resolve());
  }

  /** "Build what I earned": the payoff for the bricks banked, in the town, which then stays on screen for the end. */
  async build(landed: Promise<unknown>): Promise<void> {
    if (this.phase !== 'cards') return;
    this.phase = 'building';
    this.host.requestUpdate();
    scrollTo(0, 0);
    await landed;
    let after: OwnTown | null = null;
    try {
      after = await this.townNow();
    } catch { /* the bricks are saved all the same; only the picture is missing */ }
    const el = this.town;
    if (after && this.ok && el && this.alive) {
      el.covered = false;
      await this.then(() => el.payoff(this.bricks, after));
      const a = after;
      void this.share(this.lesson).then((s) => this.dawnAfter(el, s, a), () => {});
    }
    this.after = after;
    this.phase = 'built';
    this.host.requestUpdate();
  }

  /**
   * A tending round's end: once its answers land, the town after it is drawn (it had none behind its cards), for the
   * end's chips measured against `before`, the town as the round began.
   */
  async tendEnd(before: OwnTown | null, landed: Promise<unknown>): Promise<void> {
    if (this.phase !== 'cards') return;
    this.phase = 'building';
    this.host.requestUpdate();
    scrollTo(0, 0);
    await landed;
    this.settled = true;
    let after: OwnTown | null = null;
    try {
      after = await api.town();
    } catch { /* the round stands; only its picture is missing */ }
    if (after && this.alive) {
      // Drawn with the streets as they were, so the ones this round paved are paved on screen (`paveNew`), under the
      // sky as the round began, so a day this round earned breaks on screen (`dawnAfter`).
      const ready = this.begin(before ? streetsFrom(after, before) : after, skyOf(before));
      this.before = before;
      if (await ready) {
        const el = this.town;
        if (el) {
          el.covered = false;
          await this.then(() => el.paveNew(after!));
          if (!this.alive) return;
          await el.sync(after);
          void this.share(true).then((s) => this.dawnAfter(el, s, after!), () => {});
        }
      }
    }
    this.before = before;
    this.after = after;
    this.phase = 'built';
    this.host.requestUpdate();
  }

  /**
   * The share of today's round this sitting earned, for the dawn: the daily round's cards done over its total; a
   * tending or lesson round's passed cards over the day's round (at least one step, so any effort shows). 0 for a
   * sitting with no bricks and nothing passed.
   */
  private async share(tending: boolean): Promise<number> {
    const s = this.sitting, passed = s?.passed() ?? 0;
    if (!this.bricks.length && !passed) return 0;
    if (!tending && s) return s.total > 0 ? Math.min(1, (s.done0 + s.reached()) / s.total) : 0;
    let total = 0;
    try {
      const r = (await api.learnStatus()).round;
      total = r ? r.cards + r.done : 0;
    } catch { /* the step below stands */ }
    return total > 0 ? Math.max(Math.min(1, passed / total), 1 / 12) : 1 / 12;
  }

  /**
   * The sky eases toward first light over the town on screen by `share`, while the end's text comes; never waited on.
   * A sitting that lit the last lamp (the town after it is by day, `day` from the server) breaks the day instead.
   * A town already by day has nothing above it to go to (`dawn` skips).
   */
  private dawnAfter(el: RoundTown, share: number, after: OwnTown) {
    const target = dawnSky(skyOf(after), share, !!after.day);
    if (target > 0 && this.alive) void el.dawn(target).catch(() => {});
  }

  /** The end's chips: one over each lot among `keys` that changed this sitting; `missed`, the words missed. */
  endChips(keys: readonly string[], missed: readonly string[]): EndChip[] {
    return this.before && this.after ? endChips(this.before, this.after, keys, missed) : [];
  }

  /**
   * The town now. A lesson's town leaves out the districts that were not on screen when it began: a moment saved
   * meanwhile rises in its own landing, never in this payoff.
   */
  private async townNow(): Promise<OwnTown> {
    const now = await api.town();
    if (!this.lesson || !this.before) return now;
    const shown = new Set(this.before.districts.map((d) => d.id));
    return withoutDistricts(now, new Set(now.districts.filter((d) => !shown.has(d.id)).map((d) => d.id)));
  }

  /**
   * A save parked for Retry lands after the round has already settled (v1
   * ticket 15 review): the end's counts and chips were read (or frozen by
   * the payoff) before it arrived, so read the town again to match what the
   * server actually holds now. A no-op before the end is on screen.
   */
  async refreshChanges(): Promise<void> {
    if (!this.settled || this.phase !== 'built' || !this.before) return;
    const gen = ++this.refreshGen;
    let read: OwnTown | null = null;
    try {
      read = await this.townNow();
      if (gen !== this.refreshGen) return;
      this.after = read;
    } catch { /* the end keeps what it read */ }
    // A tended house's town is home's, drawn at the end before the late answer landed: it is drawn again to match.
    const el = this.town;
    if (this.signs && el && read && this.alive) await this.then(() => this.alive && gen === this.refreshGen && el.sync(read));
    this.host.requestUpdate();
  }

  renderTown() {
    return this.loaded ? html`<round-town></round-town>` : nothing;
  }

  /** On the cards: the bricks banked so far, and the latest, "word → material". */
  renderBank() {
    const b = this.latest;
    if (!this.bricks.length || !b) return nothing;
    return html`<div class="round-bank muted small" role="status">
      <span>${t('round.bank')}</span> <strong class="round-bank-count">${num(this.bricks.length)}</strong>
      ${keyed(`${b.key}:${b.material}:${this.bricks.length}`, html`<span class="round-brick">${brickLabel(b.text, b.material)}</span>`)}
    </div>`;
  }

  /** At the round's end: every brick banked. */
  renderBricks() {
    if (!this.bricks.length) return nothing;
    return html`<div class="round-bricks-list" style="margin-top:12px">
      <div><strong>${t('round.bank')}</strong></div>
      <div class="round-bricks">${this.bricks.map((b) => html`<span class="round-brick">${brickLabel(b.text, b.material)}</span>`)}</div>
    </div>`;
  }
}
